import { _ } from 'ajv';
import { API_URL } from '../consts.js';
import { _makeFetch, _toJson } from './_common.js';

export { KappaApiError, KappaApiUnknownError } from './_common.js';

const DEFAULT_LIMIT = 30;

function _entryToParamReducer(params, entry) {
  const [key, value] = entry;
  params.set(key, value);
  return params;
}

function _makeUrlWithSearch(urlString, searchParamsObject) {
  const url = new URL(urlString);
  const searchParams = Object.entries(searchParamsObject).reduce(
    _entryToParamReducer,
    url.searchParams
  );

  url.search = searchParams.toString();
  return url.href;
}

function _fetchGet(fullUrl) {
  return _makeFetch(fullUrl, {
    method: 'GET',
    credentials: 'include',
  }).then(_toJson);
}

function _fetchDelete(fullUrl) {
  return _makeFetch(fullUrl, {
    method: 'DELETE',
    credentials: 'include',
  });
}

function _makeJsonBodyRequest(url, method, body) {
  const headers = new Headers();
  headers.append('Content-Type', 'application/json');

  return _makeFetch(url, {
    headers,
    method,
    credentials: 'include',
    body: JSON.stringify(body),
  });
}

function _makeJsonPostRequest(url, body) {
  return _makeJsonBodyRequest(url, 'POST', body).then(_toJson);
}

function _makeJsonPatchRequest(url, body) {
  return _makeJsonBodyRequest(url, 'PATCH', body);
}

function _makeGetRequest(url, searchParams) {
  return _fetchGet(_makeUrlWithSearch(url, searchParams));
}

function _makePaginationObject(limit, offset) {
  return {
    offset: offset || 0,
    limit: limit || DEFAULT_LIMIT,
  };
}

function _makePaginatedGetRequest(url, limit, offset) {
  return _makeGetRequest(url, _makePaginationObject(limit, offset));
}

function makeCrudEndpoints(version) {
  function listLogs(endpointId, limit, offset) {
    return _makePaginatedGetRequest(
      `${API_URL}/${version}/writer/endpoint/${endpointId}/logs`,
      limit,
      offset
    );
  }

  function list(limit, offset) {
    return _makePaginatedGetRequest(
      `${API_URL}/${version}/writer/listEndpoints`,
      limit,
      offset
    );
  }

  function get(endpointId) {
    return _makeGetRequest(
      `${API_URL}/${version}/writer/endpoint/${endpointId}`,
      {}
    );
  }

  function remove(endpointId) {
    return _fetchDelete(`${API_URL}/${version}/writer/endpoint/${endpointId}`);
  }

  function patch(endpointId, model) {
    return _makeJsonPatchRequest(
      `${API_URL}/${version}/writer/endpoint/${endpointId}`,
      model
    );
  }

  function create(model) {
    return _makeJsonPostRequest(
      `${API_URL}/${version}/writer/createEndpoint`,
      model
    );
  }

  function listVersions(endpointId, limit, offset) {
    return _makePaginatedGetRequest(
      `${API_URL}/${version}/writer/endpoint/${endpointId}/changes`,
      limit,
      offset
    );
  }

  function getChange(endpointId, changeId) {
    return _makeGetRequest(
      `${API_URL}/${version}/writer/endpoint/${endpointId}/change/${changeId}`,
      {}
    );
  }

  function rollbackToChange(endpointId, changeId) {
    return _makeJsonPatchRequest(
      `${API_URL}/${version}/writer/endpoint/${endpointId}/change/${changeId}/restore`,
      {}
    );
  }

  return {
    create,
    patch,
    remove,
    get,
    list,
    listLogs,
    listVersions,
    getChange,
    rollbackToChange,
  };
}

export const v1Endpoints = makeCrudEndpoints('v1');

export const v2Endpoints = makeCrudEndpoints('v2');

export function getLruCache() {
  return _makeGetRequest(`${API_URL}/v1/writer/getLruStorage`, {});
}
